import { areaCentroid } from "./area-geometry.js";
const EPSILON = 1e-9;
/** Mononens `triarea2`: ≤ 0 heißt „c liegt links von a→b“ (bzw. gleich). */
function triarea2(a, b, c) {
    return (c.x - a.x) * (b.y - a.y) - (b.x - a.x) * (c.y - a.y);
}
const samePoint = (a, b) => Math.abs(a.x - b.x) < 1e-7 && Math.abs(a.y - b.y) < 1e-7;
function endpoints(portal) {
    return [
        { x: portal[0], y: portal[1], z: portal[2] },
        { x: portal[3], y: portal[4], z: portal[5] },
    ];
}
/** Ordnet Portal-Enden links/rechts, gesehen von einem Punkt davor. */
function orient(behind, first, second) {
    return triarea2(behind, first, second) < 0
        ? { left: second, right: first, swapped: true }
        : { left: first, right: second, swapped: false };
}
function buildPortals(asset, start, steps, goal) {
    const portals = [{ left: start, right: start }];
    for (const { fromArea, link } of steps) {
        const behind = areaCentroid(asset.areas[fromArea]);
        const [a, b] = endpoints(link.from);
        const oriented = orient(behind, a, b);
        const action = link.kind === "walk" ? undefined : link.kind;
        portals.push({
            left: oriented.left,
            right: oriented.right,
            ...(action ? { action } : {}),
        });
        if (link.land) {
            const [c, d] = endpoints(link.land);
            portals.push(oriented.swapped ? { left: d, right: c } : { left: c, right: d });
        }
    }
    portals.push({ left: goal, right: goal });
    return portals;
}
function stringPull(portals) {
    const points = [portals[0].left];
    let apex = portals[0].left;
    let left = portals[0].left;
    let right = portals[0].right;
    let leftIndex = 0;
    let rightIndex = 0;
    for (let index = 1; index < portals.length; index += 1) {
        const portal = portals[index];
        if (triarea2(apex, right, portal.right) <= 0) {
            if (samePoint(apex, right) || triarea2(apex, left, portal.right) > 0) {
                right = portal.right;
                rightIndex = index;
            }
            else {
                // Rechts schlägt über links: linke Ecke wird neuer Scheitel.
                points.push(left);
                apex = left;
                right = apex;
                rightIndex = leftIndex;
                index = leftIndex;
                continue;
            }
        }
        if (triarea2(apex, left, portal.left) >= 0) {
            if (samePoint(apex, left) || triarea2(apex, right, portal.left) < 0) {
                left = portal.left;
                leftIndex = index;
            }
            else {
                points.push(right);
                apex = right;
                left = apex;
                leftIndex = rightIndex;
                index = rightIndex;
                continue;
            }
        }
    }
    const last = portals[portals.length - 1].left;
    if (!samePoint(points[points.length - 1], last))
        points.push(last);
    return points;
}
/** Schnittpunkt von Strecke a→b mit dem Portal; `null`, wenn keiner. */
function crossing(a, b, portal) {
    const { left, right } = portal;
    const rx = b.x - a.x;
    const ry = b.y - a.y;
    const sx = right.x - left.x;
    const sy = right.y - left.y;
    const denominator = rx * sy - ry * sx;
    if (Math.abs(denominator) < EPSILON) {
        if (samePoint(left, right) && samePoint(a, left))
            return left;
        return null;
    }
    const qx = left.x - a.x;
    const qy = left.y - a.y;
    const t = (qx * sy - qy * sx) / denominator;
    const u = (qx * ry - qy * rx) / denominator;
    const tolerance = 1e-6;
    if (t < -tolerance ||
        t > 1 + tolerance ||
        u < -tolerance ||
        u > 1 + tolerance)
        return null;
    const along = Math.min(1, Math.max(0, u));
    return {
        x: a.x + rx * Math.min(1, Math.max(0, t)),
        y: a.y + ry * Math.min(1, Math.max(0, t)),
        z: left.z + (right.z - left.z) * along,
    };
}
/**
 * Gerader Pfad durch die Portale, mit einem Punkt je Portal-Durchgang:
 * so stimmt z an jedem Ebenenwechsel und Aktionen sitzen genau an der Kante.
 */
export function funnelPath(asset, start, steps, goal) {
    const startVertex = { x: start.x, y: start.y, z: start.z };
    const goalVertex = { x: goal.x, y: goal.y, z: goal.z };
    const portals = buildPortals(asset, startVertex, steps, goalVertex);
    const corners = stringPull(portals);
    const result = [startVertex];
    const push = (point) => {
        const last = result[result.length - 1];
        if (samePoint(last, point) && Math.abs(last.z - point.z) < 0.5) {
            if (point.action)
                last.action = point.action;
            return;
        }
        result.push(point);
    };
    let segment = 0;
    for (let index = 1; index < portals.length - 1; index += 1) {
        const portal = portals[index];
        let hit = null;
        while (segment < corners.length - 1) {
            hit = crossing(corners[segment], corners[segment + 1], portal);
            if (hit)
                break;
            segment += 1;
            if (segment < corners.length - 1)
                push({ ...corners[segment] });
        }
        if (!hit) {
            // Numerischer Rand: Portalmitte statt Pfadabbruch.
            hit = {
                x: (portal.left.x + portal.right.x) / 2,
                y: (portal.left.y + portal.right.y) / 2,
                z: (portal.left.z + portal.right.z) / 2,
            };
            segment = Math.max(0, corners.length - 2);
        }
        push(portal.action ? { ...hit, action: portal.action } : hit);
    }
    push(goalVertex);
    return result;
}
